import { useTeamContext } from '@/context/TeamContext';
import { useLimits, LimitDetail } from '@/hooks/useLimits';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, AlertTriangle, AlertOctagon, XCircle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { getLimitLabel } from '@/constants/limitTypes';
import { useTranslation } from '@/hooks/useTranslation';
import { hasUnlimitedCoreLimits } from '@/lib/planHelpers';

interface SidebarLimitIndicatorProps {
  isCollapsed: boolean;
}

export function SidebarLimitIndicator({ isCollapsed }: SidebarLimitIndicatorProps) {
  const { t } = useTranslation();
  const { teamId } = useTeamContext();
  const { data: limitsData, isLoading, error } = useLimits(teamId);
  const navigate = useNavigate();

  // Show loading skeleton while loading
  if (isLoading) {
    return isCollapsed ? (
      <Skeleton className="h-8 w-8 rounded-md" />
    ) : (
      <div className="flex items-center gap-3 px-3 py-2">
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-5 w-8 rounded-full" />
      </div>
    );
  }

  // Don't show anything if pro plan or no issues
  if (!limitsData || hasUnlimitedCoreLimits(limitsData.planId) || !limitsData.hasAnyLimitIssues) {
    return null;
  }

  const { overallStatus, limits, criticalLimits } = limitsData;

  // Get icon and color based on overall status
  const getStatusConfig = () => {
    switch (overallStatus) {
      case 'blocked':
        return {
          icon: XCircle,
          color: 'bg-red-500',
          textColor: 'text-red-700 dark:text-red-300',
          badgeVariant: 'destructive' as const,
          label: t('sidebarLimitIndicator.blocked'),
        };
      case 'critical':
        return {
          icon: AlertOctagon,
          color: 'bg-orange-500',
          textColor: 'text-orange-700 dark:text-orange-300',
          badgeVariant: 'default' as const,
          label: t('sidebarLimitIndicator.critical'),
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          color: 'bg-amber-500',
          textColor: 'text-amber-700 dark:text-amber-300',
          badgeVariant: 'default' as const,
          label: t('sidebarLimitIndicator.warningLabel'),
        };
      default:
        return {
          icon: AlertCircle,
          color: 'bg-blue-500',
          textColor: 'text-blue-700 dark:text-blue-300',
          badgeVariant: 'default' as const,
          label: t('sidebarLimitIndicator.info'),
        };
    }
  };

  const statusConfig = getStatusConfig();
  const Icon = statusConfig.icon;

  // Filter to only numeric limits (exclude feature flags)
  const numericLimits = limits.filter(l => 'currentUsage' in l && 'maxAllowed' in l);

  // Get the most critical limit to display inline
  const mostCriticalLimit = numericLimits
    .filter(l => l.warningLevel !== 'ok')
    .sort((a, b) => {
      const order = { blocked: 0, critical: 0, warning: 1, info: 2, ok: 3 };
      const orderA = order[a.warningLevel as keyof typeof order] ?? 4;
      const orderB = order[b.warningLevel as keyof typeof order] ?? 4;
      if (orderA !== orderB) return orderA - orderB;
      return (b.percentage ?? 0) - (a.percentage ?? 0);
    })[0];

  // Count issues by severity
  const criticalCount = numericLimits.filter(l => l.warningLevel === 'critical' || l.warningLevel === 'blocked').length;
  const warningCount = numericLimits.filter(l => l.warningLevel === 'warning').length;
  const infoCount = numericLimits.filter(l => l.warningLevel === 'info').length;

  // Check if there are multiple limits in warning state
  const multipleLimitsWarning = numericLimits.filter(l => l.warningLevel !== 'ok').length > 1;

  const handleUpgradeClick = () => {
    navigate('/settings?tab=billing');
  };

  // Collapsed view: Just show a dot indicator with popover
  if (isCollapsed) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'relative flex items-center justify-center w-8 h-8 rounded-lg transition-colors',
              'hover:bg-gray-100 dark:hover:bg-gray-800'
            )}
          >
            <Icon className={cn('h-4 w-4', statusConfig.textColor)} />
            <span className={cn(
              'absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900',
              statusConfig.color
            )} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-80">
          <LimitDetailsPopover
            limits={limits}
            overallStatus={overallStatus}
            criticalCount={criticalCount}
            warningCount={warningCount}
            infoCount={infoCount}
            onUpgradeClick={handleUpgradeClick}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Expanded view: Show inline critical limit with optional "view all" popover
  return (
    <div className="px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
      {mostCriticalLimit && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon className={cn('h-4 w-4', statusConfig.textColor)} />
            <span className="text-xs font-medium">{t('sidebarLimitIndicator.usageLimits')}</span>
          </div>

          {/* Show most critical limit inline */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {getLimitLabel(mostCriticalLimit.limitType)}
              </span>
              <span className="text-xs font-semibold font-mono">
                {mostCriticalLimit.currentUsage}/{mostCriticalLimit.maxAllowed}
              </span>
            </div>
            <Progress
              value={mostCriticalLimit.percentage ?? 0}
              className={cn(
                "h-1",
                mostCriticalLimit.percentage >= 100 && "bg-red-100 dark:bg-red-950 [&>div]:bg-red-500",
                mostCriticalLimit.percentage >= 90 && mostCriticalLimit.percentage < 100 && "bg-orange-100 dark:bg-orange-950 [&>div]:bg-orange-500",
                mostCriticalLimit.percentage >= 75 && mostCriticalLimit.percentage < 90 && "bg-amber-100 dark:bg-amber-950 [&>div]:bg-amber-500",
                mostCriticalLimit.percentage >= 50 && mostCriticalLimit.percentage < 75 && "bg-blue-100 dark:bg-blue-950 [&>div]:bg-blue-500"
              )}
            />
          </div>

          {/* Only show popover for "see all limits" if multiple limits are approaching */}
          {multipleLimitsWarning && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-xs text-primary hover:underline text-left">
                  {t('sidebarLimitIndicator.viewAllLimits', { count: numericLimits.filter(l => l.warningLevel !== 'ok').length })}
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="w-80">
                <LimitDetailsPopover
                  limits={limits}
                  overallStatus={overallStatus}
                  criticalCount={criticalCount}
                  warningCount={warningCount}
                  infoCount={infoCount}
                  onUpgradeClick={handleUpgradeClick}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  );
}

// Internal component for the popover content
function LimitDetailsPopover({
  limits,
  overallStatus,
  criticalCount,
  warningCount,
  infoCount,
  onUpgradeClick,
}: {
  limits: LimitDetail[];
  overallStatus: string;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  onUpgradeClick: () => void;
}) {
  const { t } = useTranslation();
  const getStatusIcon = (level: string) => {
    switch (level) {
      case 'blocked':
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'info':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (level: string) => {
    switch (level) {
      case 'blocked':
      case 'critical':
        return 'text-red-700 dark:text-red-300';
      case 'warning':
        return 'text-amber-700 dark:text-amber-300';
      case 'info':
        return 'text-blue-700 dark:text-blue-300';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h4 className="font-semibold text-sm">{t('sidebarLimitIndicator.planUsageStatus')}</h4>
        <p className="text-xs text-muted-foreground">
          {/* {criticalCount > 0 && (criticalCount > 1 ? t('sidebarLimitIndicator.criticalLimitPlural', { count: criticalCount }) : t('sidebarLimitIndicator.criticalLimit', { count: criticalCount }))} */}
          {criticalCount > 0 && warningCount > 0 && ', '}
          {warningCount > 0 && (warningCount > 1 ? t('sidebarLimitIndicator.warningPlural', { count: warningCount }) : t('sidebarLimitIndicator.warning', { count: warningCount }))}
          {(criticalCount > 0 || warningCount > 0) && infoCount > 0 && ', '}
          {infoCount > 0 && t('sidebarLimitIndicator.approaching', { count: infoCount })}
        </p>
      </div>

      <div className="space-y-2">
        {limits
          .filter(limit => limit.warningLevel !== 'ok')
          .filter(limit => 'currentUsage' in limit && 'maxAllowed' in limit) // Only show numeric limits, not feature flags
          .sort((a, b) => {
            const order = { blocked: 0, critical: 0, warning: 1, info: 2, ok: 3 };
            return order[a.warningLevel as keyof typeof order] - order[b.warningLevel as keyof typeof order];
          })
          .map((limit) => (
            <div
              key={limit.limitType}
              className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50"
            >
              <div className="flex items-center gap-2 min-w-0">
                {getStatusIcon(limit.warningLevel)}
                <span className="text-xs font-medium truncate">
                  {getLimitLabel(limit.limitType)}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={cn('text-xs font-mono', getStatusColor(limit.warningLevel))}>
                  {limit.currentUsage}/{limit.maxAllowed}
                </span>
                <span className={cn('text-xs font-semibold', getStatusColor(limit.warningLevel))}>
                  {(limit.percentage ?? 0).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
      </div>

      <Button
        onClick={onUpgradeClick}
        size="sm"
        className={cn(
          'w-full gap-2',
          overallStatus === 'blocked' || overallStatus === 'critical'
            ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700'
            : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
        )}
      >
        {t('sidebarLimitIndicator.upgradeToPro')}
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
